import { useEffect, useRef, useState } from 'react';
import { RUTA_DE_VISTA } from '../rutas.js';

/**
 * Navegacion principal.
 *
 * UNA SOLA LISTA, DOS FORMAS. En escritorio son pestañas arriba; en telefono, una
 * barra fija abajo con iconos. El mismo array alimenta las dos, asi que añadir
 * una seccion es añadir una entrada y no mantener dos menus que se desincronizan.
 *
 * Abajo y no arriba en el telefono porque ahi llega el pulgar. Y FIJA: la
 * navegacion no puede irse con el scroll -- en una lista de 4.700 cartas, tener
 * que subir hasta arriba para cambiar de seccion es lo que hace que una web se
 * sienta web y una app se sienta app.
 */

const Icon = ({ d, filled = false }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="nav__icon">
    <path
      d={d}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={filled ? 'currentColor' : 'none'}
    />
  </svg>
);

/** Trazos de 24 px, grosor 1.7: la convencion del resto de iconos del proyecto. */
const PATHS = {
  cards: 'M4 7a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Zm13 1 2.6.7a2 2 0 0 1 1.4 2.5l-2 7.3',
  /*
   * Con el meta en la portada, Cartas y Mazos quedan pegadas en la barra y sus
   * iconos eran dos rectangulos casi iguales. El mazo pasa a ser una PILA en
   * perspectiva (capas): silueta romboidal que no se confunde con la carta
   * suelta de al lado.
   */
  /*
   * OJO: la clave es `decks` (plural), la MISMA que usa la pestaña. Se llamaba
   * `deck` y nadie casaba: la pestaña Mazos caia al icono de respaldo -- el de
   * Cartas -- y por eso las dos se veian identicas. El fallback esconde este
   * fallo en silencio, asi que si se añade una seccion hay que comprobar que su
   * icono de verdad aparece.
   */
  decks: 'M12 4 3.5 8.3l8.5 4.2 8.5-4.2L12 4ZM3.5 12.7l8.5 4.2 8.5-4.2M3.5 17.1l8.5 4.2 8.5-4.2',
  import: 'M12 3v11m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
  meta: 'M4 20V10m5 10V4m5 16v-7m5 7V8',
  matches: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-14v5l3 2',
  admin: 'M12 3 4 6.5v5c0 4.4 3.1 8.4 8 9.5 4.9-1.1 8-5.1 8-9.5v-5L12 3Z',
};

/**
 * `brand` y `tools` son los extremos de la cabecera (logo+nombre y
 * lupa/datos/campana/cuenta): viven en App, que es quien tiene su estado, y
 * se pintan AQUI porque la barra es UNA. Antes eran dos franjas -- la marca
 * arriba, las pestañas debajo -- y la de arriba se iba con el scroll: dos
 * alturas de cabecera para una sola navegacion, y el buscador y la cuenta
 * desaparecian justo al desplazarse. Fusionadas, la zona pegajosa lleva todo.
 */
export default function Nav({ items, view, onChange, brand, tools }) {
  /**
   * Con la pagina desplazada, la pildora flotante pasaba POR ENCIMA del
   * contenido y se camuflaba: era una ISLA angosta y todo lo que asomaba por
   * los lados -- filas de tabla, tarjetas -- pasaba a contraste completo, y
   * habia usuarios que subian hasta arriba buscando el menu sin ver que
   * seguia ahi. La respuesta no es solo oscurecer la pildora: al quedarse
   * pegada, su zona entera (el ancho completo) pinta un velo que desenfoca y
   * funde el contenido antes de tocarla. Arriba siempre hay una franja que ES
   * navegacion, no contenido, y el ojo la encuentra sin buscar. En reposo el
   * velo no existe y el cristal ligero luce como siempre.
   *
   * `elevada` se decide midiendo si la zona esta PEGADA (top <= 0), no con un
   * umbral de scrollY a ojo: el alto de la cabecera cambia con la ventana y
   * el umbral fijo elevaba la barra antes de tiempo.
   */
  const [elevada, setElevada] = useState(false);
  const zonaRef = useRef(null);
  useEffect(() => {
    const mirar = () => {
      const el = zonaRef.current;
      if (el) setElevada(el.getBoundingClientRect().top <= 0);
    };
    mirar();
    window.addEventListener('scroll', mirar, { passive: true });
    window.addEventListener('resize', mirar);
    return () => {
      window.removeEventListener('scroll', mirar);
      window.removeEventListener('resize', mirar);
    };
  }, []);

  /**
   * Publica el alto de la franja de pestañas como `--nav-alto`, hermano de
   * `--botnav-alto` y por el mismo motivo: cualquier OTRO elemento pegajoso
   * (el mando de Partidas) tiene que anclarse DEBAJO de ella, y clavando el
   * numero a mano se esconderia detras en cuanto la franja cambie de alto
   * -- con el idioma, con la ventana, o el dia que se retoque su relleno.
   *
   * En movil la franja no existe (dock inferior): alto 0, y quien se ancle
   * a la variable vuelve solo al borde de la ventana.
   */
  useEffect(() => {
    const el = zonaRef.current;
    if (!el) return undefined;
    const medir = () => {
      /* Solo cuenta si la barra esta PEGAJOSA. En el telefono la cabecera
         existe (marca + herramientas) pero es estatica y se va con el
         scroll: publicar su alto clavaria la banda del meta y el mando de
         Partidas 50 px por debajo del borde, colgando de una barra que ya
         no esta. Antes el movil publicaba 0 porque la zona iba en
         display:none; ahora lo decide la posicion computada. */
      const pegajosa = getComputedStyle(el).position === 'sticky';
      const alto = pegajosa ? Math.round(el.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--nav-alto', `${alto}px`);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, []);
  /**
   * Publica el espacio real que ocupa la barra inferior como `--botnav-alto`.
   *
   * Cualquier cosa que quiera flotar encima de ella -- el "deshacer" del
   * historial, por ejemplo -- necesita saber cuanto ocupa. Calcularlo a mano
   * sale mal: el numero obvio son los 48 px de `min-height` del boton, y en un
   * iPhone cambia otra vez con el hueco del gesto de inicio.
   *
   * Desde que la barra FLOTA separada del borde, el alto del elemento ya no
   * basta: lo que importa es la distancia del borde inferior de la ventana a la
   * parte alta de la barra (alto + hueco flotante + safe-area). Se mide del
   * elemento de verdad y se observa: si algun dia cambia, lo que flote encima
   * se entera solo en vez de quedarse desfasado.
   */
  const barraRef = useRef(null);
  useEffect(() => {
    const el = barraRef.current;
    if (!el) return;
    const medir = () => {
      /* OJO: `offsetParent` no sirve para saber si se ve -- un elemento
         `position: fixed` devuelve null SIEMPRE. Se pregunta por el alto del
         rectangulo: cero significa display:none (escritorio). */
      const rect = el.getBoundingClientRect();
      const alto = rect.height
        ? Math.max(0, Math.round(window.innerHeight - rect.top))
        : 0;
      document.documentElement.style.setProperty('--botnav-alto', `${alto}px`);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, []);

  /*
   * La pildora deslizante de la barra inferior. Es UN elemento que viaja entre
   * pestañas en vez de un fondo que aparece y desaparece en cada boton: el ojo
   * sigue el movimiento y entiende "me he movido de aqui a alla", que es lo que
   * una transicion debe contar. Posicion y ancho salen del indice, en
   * porcentaje, asi que no hay nada que medir ni recalcular al girar el
   * telefono.
   */
  const idx = Math.max(0, items.findIndex((tab) => tab.key === view));
  const n = items.length || 1;

  /*
   * Su hermana de escritorio no puede salir de un indice: cada pestaña mide lo
   * que mide su rotulo, asi que la posicion se MIDE del boton activo real y se
   * publica como variables en el contenedor. La pildora las anima con
   * transform, que no relanza layout. Se re-mide en cada render (los rotulos
   * cambian con el idioma) y al redimensionar; hasta la primera medida la
   * pildora va oculta para no verla nacer en la esquina.
   */
  const tabsRef = useRef(null);
  useEffect(() => {
    const cont = tabsRef.current;
    if (!cont) return undefined;
    const medir = () => {
      const activo = cont.querySelector('.tabs__btn.is-active');
      if (!activo) return;
      cont.style.setProperty('--tab-x', `${activo.offsetLeft}px`);
      cont.style.setProperty('--tab-w', `${activo.offsetWidth}px`);
      cont.classList.add('is-medida');
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(cont);
    return () => ro.disconnect();
  });

  return (
    <>
      {/* En escritorio la barra habla el mismo idioma que el dock del telefono:
          pildora flotante, icono + rotulo, y el resalte dorado VIAJA de una
          pestaña a otra en vez de aparecer en seco. La ZONA (y no la pildora)
          es quien se queda pegada: al elevarse pinta el velo de ancho completo
          que separa navegacion de contenido. */}
      {/*
        NO es un `tablist`, aunque lo parezca.

        Llevaba `role=tablist` y `role=tab` con `aria-selected`, y eso le decia
        a un lector de pantalla una cosa que no es cierta: que esto son fichas
        que cambian un panel dentro de la misma pagina. Son ENLACES a paginas
        distintas -- cada seccion tiene su ruta, su titulo y su entrada de
        historial --, y el rol de ficha tapaba justamente eso: se anunciaban
        como "pestaña 2 de 5" en vez de como enlaces, y quien navega con lector
        no tenia forma de saber que podia abrirlos en otra pestaña.

        Un `nav` con enlaces y `aria-current="page"` en el activo dice la
        verdad y no necesita nada mas. El aspecto no cambia: el resalte
        siempre salio de `.is-active`, nunca del aria.
      */}
      {/* CABECERA UNICA: marca | pestañas | herramientas, y la zona entera es
          la pegajosa -- el buscador, la campana y la cuenta viajan con el
          scroll igual que las pestañas. Lleva las DOS clases a proposito:
          `topbar` pone la fila (y sus reglas de telefono siguen valiendo tal
          cual) y `tabszona` pone lo pegajoso y el velo. Es <header> porque ES
          la cabecera del documento: el landmark banner es este. */}
      <header className={`topbar tabszona${elevada ? ' is-elevada' : ''}`} ref={zonaRef}>
      {brand}
      <nav
        className="tabs"
        aria-label="Secciones"
        ref={tabsRef}
      >
        <span className="tabs__pildora" aria-hidden="true" />
        {/*
          Enlaces DE VERDAD, no botones: cada seccion tiene ruta propia y el
          href es lo que un robot puede seguir sin ejecutar nada -- sin esto,
          para Google las secciones no existen. El clic de una persona se
          intercepta y navega la SPA como siempre (sin recarga); ctrl+clic y
          "abrir en pestaña nueva" funcionan gratis por ser enlaces.
        */}
        {items.map((tab) => (
          <a
            key={tab.key}
            href={RUTA_DE_VISTA[tab.key] ?? '/'}
            aria-current={view === tab.key ? 'page' : undefined}
            className={`tabs__btn${view === tab.key ? ' is-active' : ''}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              onChange(tab.key);
            }}
          >
            <Icon d={PATHS[tab.key] ?? PATHS.cards} />
            {tab.label}
          </a>
        ))}
      </nav>
      {tools}
      </header>

      {/*
        EL SUELO DEL DOCK. Decoracion pura y por eso va escondido de los
        lectores: una franja que funde el contenido antes de llegar abajo, para
        que la parte baja de la pantalla se lea como navegacion y no como una
        tarjeta mas. Habia gente que se quedaba en la portada sin ver el menu.

        Elemento propio y no un `::before` del dock: un pseudo con z-index
        negativo no se va detras del fondo de un padre que crea contexto de
        apilamiento -- y el dock lo crea --, asi que acababa oscureciendo justo
        lo que venia a destacar. El porque largo esta en la hoja de estilos.
      */}
      <div className="botnav__suelo" aria-hidden="true" />

      {/* Barra inferior: el mismo menú que arriba, sólo cambia dónde se dibuja
          -- y por eso también es navegación y no un `tablist` (ver arriba). */}
      <nav className="botnav" aria-label="Secciones" ref={barraRef}>
        {/* La pildora es decoracion pura: los estados accesibles viven en los
            botones (aria-selected), asi que se esconde de los lectores. */}
        <span
          className="botnav__pildora"
          aria-hidden="true"
          style={{ '--nav-i': idx, '--nav-n': n }}
        />
        {/* Enlaces por lo mismo que arriba: mismo menu, misma razon. */}
        {items.map((tab) => (
          <a
            key={tab.key}
            href={RUTA_DE_VISTA[tab.key] ?? '/'}
            aria-current={view === tab.key ? 'page' : undefined}
            className={`botnav__btn${view === tab.key ? ' is-active' : ''}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              onChange(tab.key);
            }}
          >
            <Icon d={PATHS[tab.key] ?? PATHS.cards} />
            <span className="botnav__label">{tab.short ?? tab.label}</span>
          </a>
        ))}
      </nav>
    </>
  );
}
